import React, { useEffect, useState } from "react";
import { Camera, Trash2, Images } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const galleryUrl = (itemId, which) => `${BACKEND}/api/shop/items/${itemId}/photo${which ? `?which=${encodeURIComponent(which)}` : ""}`;

export function galleryFromItem(item, loan) {
  const out = [];
  if (item.photo_path) out.push({ which: "", label: "intake" });
  if (item.receipt_photo_path) out.push({ which: "receipt", label: "warehouse inspection" });
  (item.photos || []).forEach((p) => out.push({ which: `extra:${p.id}`, id: p.id, label: p.label || "extra", created_at: p.created_at }));
  if (loan?.release_info?.photo_path) out.push({ which: "release", label: "release" });
  return out;
}

export function PhotoStrip({ item, photos, size = "h-20 w-20", testid = "photo-strip" }) {
  const list = photos || galleryFromItem(item);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {list.map((p) => (
        <figure key={p.which} className="text-center">
          <img src={galleryUrl(item.id, p.which)} alt={p.label} className={`${size} rounded object-cover border border-slate-200`} />
          <figcaption className="text-[9px] text-slate-500 mt-0.5 max-w-20 truncate">{p.label}</figcaption>
        </figure>
      ))}
    </div>
  );
}

export function ItemGalleryDialog({ item, onClose, managerUp, onChanged }) {
  const { t } = useLang();
  const [photos, setPhotos] = useState(null);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => item && API.get(`/shop/items/${item.id}/gallery`).then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPhotos(null); load(); }, [item?.id]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      await API.post(`/shop/items/${item.id}/photos`, fd);
      toast.success(t("gal.added"));
      setFile(null); setLabel("");
      load(); onChanged && onChanged();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try { await API.delete(`/shop/items/${item.id}/photos/${id}`); load(); onChanged && onChanged(); } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gallery-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Images className="h-5 w-5" />{t("gal.title")} · {item?.name}</DialogTitle></DialogHeader>
        {photos && (photos.length === 0 ? <p className="text-sm text-slate-500" data-testid="gallery-empty">{t("gal.empty")}</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-grid">
            {photos.map((p) => (
              <figure key={p.which} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`gallery-photo-${p.id || p.which || "intake"}`}>
                <a href={galleryUrl(item.id, p.which)} target="_blank" rel="noreferrer">
                  <img src={galleryUrl(item.id, p.which)} alt={p.label} className="h-36 w-full object-cover" />
                </a>
                <figcaption className="p-2 text-xs flex items-center justify-between gap-2">
                  <span className="truncate"><span className="font-medium">{p.label}</span>{p.created_at ? <span className="text-slate-400"> · {p.created_at.slice(0, 10)}</span> : null}</span>
                  {managerUp && p.id && <button onClick={() => remove(p.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`gallery-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>}
                </figcaption>
              </figure>
            ))}
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3 mt-2">
          <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs hover:bg-slate-50">
            <Camera className="h-3.5 w-3.5" />{file ? file.name : t("items.photo_capture")}
            <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="gallery-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <Input data-testid="gallery-label-input" placeholder={t("gal.label_ph")} value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 flex-1 min-w-40" />
          <Button data-testid="gallery-upload-button" onClick={upload} disabled={!file || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">{t("gal.add")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
